import React, { Fragment, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import queryKeys from 'config/queryKeys';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import Cookies from 'universal-cookie';
import theme from 'theme';

// API
import * as remoteApi from 'api/remote';

// Routes
import * as routes from 'routes';

// Components
import AccessDenied from 'components/AccessDenied';
import Loader from 'components/Loader';
import { ChromeContent } from 'components/Chrome';
import NotFound from 'components/NotFound';
import FormField from 'components/FormField';
import Wordmark from 'components/Wordmark';

// Tracker component
// import Tracker from './Tracker';
import KoalaButton from 'koala/components/Button';
import PublicPlanPresentationOverview from './PublicPlanPresentationOverview';

const LoadingContainer = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const AccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  align-items: center;
  justify-content: center;

  h2 {
    margin-bottom: ${theme.spacing.x3};
  }

  form {
    padding-bottom: ${theme.spacing.x8};
    width: 40rem;

    button {
      margin-right: ${theme.spacing.x2};
    }
    .danger {
      color: ${theme.colors.red};
    }
  }
`;

const WordmarkWrapper = styled.div`
  margin-bottom: ${theme.spacing.x8};
`;

function PublicPlanPresentation() {
  const cookies = new Cookies();
  const { planId, workspaceSlug } = useParams<{ planId: string; workspaceSlug: string }>();
  const location = useLocation();

  const COOKIENAME = '_tbty_pp';
  const COOKIEPATH = location.pathname;

  const public_access_password = cookies.get(COOKIENAME);

  const params = {
    public_access_password,
  };

  // Construct the query key using the plan Id
  const queryKey = [queryKeys.currentPlan, planId, params];
  const staleTime = 0;
  const [accessDenied, setAccessDenied] = useState(false);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [password, setPassword] = useState('');

  // Get the plan details
  const {
    isError,
    error,
    data: planResponse,
    isLoading: planIsLoading,
  }: any = useQuery(queryKey, remoteApi.fetchTemplateDetails, {
    staleTime,
    onError: (error: any) => {
      const errorString = error.response.data.error;
      const accessDenied = errorString.includes('Public access is disabled');
      setAccessDenied(accessDenied);
      const passwordRequired = errorString.includes('Password is missing');
      setPasswordRequired(passwordRequired);
      if (public_access_password) {
        setPasswordError(true);
      }
    },
  });

  const planPath = routes.WORKSPACE_PLAN_TRACK_ROUTE.replace(':workspaceSlug', workspaceSlug).replace(
    ':planId',
    planId,
  );

  const plan = planResponse ? planResponse.data : null;
  const dataIsLoading = planIsLoading;

  // We display the loader until we have loaded the plan and objectives.
  // The reason why we wait for objectives to be loaded here is to check in the Track components
  // if we should display the empty state, or the tracking content
  if (dataIsLoading) {
    return (
      <Fragment>
        <ChromeContent>
          <LoadingContainer>
            <Loader size="big" />
          </LoadingContainer>
        </ChromeContent>
      </Fragment>
    );
  }

  if (accessDenied) {
    return (
      <Fragment>
        <Helmet>
          <title>Access restricted | Tability</title>
        </Helmet>
        <AccessContainer>
          <WordmarkWrapper>
            <Wordmark type="dark" />
          </WordmarkWrapper>

          <h2>Public access is restricted for this plan</h2>
          <p>Enable public sharing on your plan to view a digest report without having to sign in.</p>
          <p>
            <KoalaButton isLink to={planPath} appearance="subtle">
              Back to your workspace
            </KoalaButton>
          </p>
        </AccessContainer>
      </Fragment>
    );
  }

  const handlePasswordChange = (e: any) => {
    const password = e.target.value;
    setPassword(password);
  };

  const handleSavePassword = (e: any) => {
    e.preventDefault();
    cookies.set(COOKIENAME, password, {
      path: COOKIEPATH,
      secure: true,
    });
    window.location.reload();
  };

  if (passwordRequired) {
    return (
      <Fragment>
        <Helmet>
          <title>Access restricted | Tability</title>
        </Helmet>
        <AccessContainer>
          <WordmarkWrapper>
            <Wordmark type="dark" />
          </WordmarkWrapper>

          <h2>A password is required to access this plan</h2>
          <form onSubmit={handleSavePassword}>
            <FormField>
              <label>Password</label>
              <input type="password" value={password} onChange={handlePasswordChange} />
            </FormField>
            {passwordError && (
              <FormField>
                <span className="danger">Sorry, your password did not match the records.</span>
              </FormField>
            )}
            <FormField>
              <KoalaButton submit>Submit</KoalaButton>
              <KoalaButton isLink to="/" appearance="subtle">
                Return to Tability
              </KoalaButton>
            </FormField>
          </form>
        </AccessContainer>
      </Fragment>
    );
  }

  if (!plan) {
    return (
      <Fragment>
        <ChromeContent>
          <LoadingContainer>
            <Loader size="big" />
          </LoadingContainer>
        </ChromeContent>
      </Fragment>
    );
  }

  if (isError) {
    const errorResponse = error.response;
    if (errorResponse.status === 404) {
      return <NotFound />;
    }
    if (errorResponse.status === 401) {
      return <AccessDenied />;
    }
    return <NotFound />;
  }

  return (
    <Fragment>
      <Helmet>
        <title>Template: {plan.title} | Tability</title>
      </Helmet>
      <PublicPlanPresentationOverview plan={plan} />
    </Fragment>
  );
}

export default PublicPlanPresentation;
